import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDashboard } from '../hooks/useDashboard';
import PeriodSelector from '../components/PeriodSelector';
import SummaryCard from '../components/SummaryCard';
import ClusterCard from '../components/ClusterCard';
import EmergingTrendsCard from '../components/EmergingTrendsCard';
import { RegionalSummary } from '../components/RegionalBreakdown';
import StatusBadge from '../components/StatusBadge';
import { SkeletonSummaryCard, SkeletonClusterCard, SkeletonCard } from '../components/Skeleton';
import api from '../api/client';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function ExportMenu({ period }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const downloadCsv = (type) => {
    // Authenticated blob download — window.open can't send the JWT header (→401).
    // Report failures: a silently swallowed rejection is indistinguishable from
    // a download that never started.
    api.exportDashboardCsv(type, period).catch((err) => {
      setSendResult(err?.status === 403 ? 'Export needs leadership or admin access' : 'Export failed');
      setTimeout(() => setSendResult(null), 3000);
    });
    setOpen(false);
  };

  const sendFeishuDigest = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API_BASE}/digest/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_to_feishu: true }),
      });
      const data = await res.json();
      setSendResult(data.success ? 'Digest sent to Feishu' : 'Failed to send');
    } catch (err) {
      if (import.meta.env?.DEV) console.error('Feishu digest send failed:', err);
      setSendResult('Failed to send');
    } finally {
      setSending(false);
      setTimeout(() => setSendResult(null), 3000);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', fontSize: 13, fontWeight: 600,
          color: 'var(--text-primary)', background: 'var(--bg-tertiary)',
          border: '1px solid var(--border-color)', borderRadius: 8,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 6,
          background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          minWidth: 220, zIndex: 50, overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Download CSV
          </div>
          <button onClick={() => downloadCsv('clusters')} style={menuItemStyle}>
            <span>Clusters & Requests</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ranked list with status</span>
          </button>
          <button onClick={() => downloadCsv('tickets')} style={menuItemStyle}>
            <span>Raw Tickets</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>All tickets for period</span>
          </button>
          <button onClick={() => downloadCsv('digest')} style={menuItemStyle}>
            <span>Monthly Digest</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Top 50 ranked requests</span>
          </button>
          <div style={{ borderTop: '1px solid var(--border-color)', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Share
          </div>
          <button onClick={sendFeishuDigest} disabled={sending} style={{ ...menuItemStyle, opacity: sending ? 0.6 : 1 }}>
            <span>{sending ? 'Sending...' : 'Send Digest to Feishu'}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Monthly summary card</span>
          </button>
          {sendResult && (
            <div style={{ padding: '8px 14px', fontSize: 12, color: sendResult.includes('Failed') ? 'var(--color-error)' : 'var(--color-success)', fontWeight: 600 }}>
              {sendResult}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const menuItemStyle = {
  display: 'flex', flexDirection: 'column', gap: 2,
  width: '100%', textAlign: 'left', padding: '10px 14px',
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
  color: 'var(--text-primary)',
  transition: 'background 0.1s',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function RecentActivityCard() {
  const [actions, setActions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.actionLog({ limit: 5 }).then(res => {
      setActions(res.data?.actions || []);
      setLoaded(true);
    }).catch((err) => {
      if (import.meta.env?.DEV) console.error('Failed to load recent activity:', err);
      setLoaded(true);
    });
  }, []);

  if (!loaded) return null;
  if (actions.length === 0) {
    return (
      <div className="card">
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Recent Activity</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No activity yet</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Recent Activity</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {actions.map((action) => (
          <div key={action.id} style={{ fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Link
                to={`/clusters/${action.cluster_id}`}
                style={{ color: 'var(--text-primary)', fontWeight: 600, textDecoration: 'none' }}
              >
                {action.cluster_name}
              </Link>
              {action.new_value && <StatusBadge status={action.new_value} size="xs" showDot={false} />}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
              {action.performed_by} · {timeAgo(action.performed_at)}
            </div>
          </div>
        ))}
      </div>
      <Link
        to="/activity"
        style={{ display: 'block', marginTop: 12, fontSize: 12, fontWeight: 600, color: 'var(--coros-red)', textDecoration: 'none' }}
      >
        View all activity →
      </Link>
    </div>
  );
}

const STATUS_FILTERS = {
  needs_attention: {
    label: 'Needs Attention',
    statuses: ['new', 'active', 'more_evidence_needed'],
    description: 'Items needing review or decision',
  },
  tracked: {
    label: 'Tracked',
    statuses: ['under_consideration', 'in_progress', 'acknowledged'],
    description: 'Items already being tracked',
  },
  all: {
    label: 'All',
    statuses: null, // show everything
    description: 'All ranked clusters',
  },
};

function StatusFilterTabs({ value, onChange, counts }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {Object.entries(STATUS_FILTERS).map(([key, filter]) => {
        const isActive = value === key;
        const count = counts[key] || 0;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 12px', borderRadius: 6,
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              cursor: 'pointer', transition: 'all 0.15s',
              background: isActive ? 'var(--coros-red)' : 'var(--bg-tertiary)',
              color: isActive ? '#fff' : 'var(--text-muted)',
              border: isActive ? '1px solid var(--coros-red)' : '1px solid var(--border-color)',
            }}
          >
            {filter.label}
            <span style={{
              fontSize: 10, fontWeight: 700,
              background: isActive ? 'rgba(255,255,255,0.2)' : 'var(--bg-hover)',
              padding: '0 5px', borderRadius: 10,
            }}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function Dashboard() {
  // Default to 'last_month' — the current, actionable window. Recent periods are
  // kept populated by the weekly clustering refresh (cron: Sun 02:00 UTC).
  const { data, loading, error, period, changePeriod } = useDashboard('last_month');
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [showAllIssues, setShowAllIssues] = useState(false);
  const [statusFilter, setStatusFilter] = useState('needs_attention');

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header skeleton */}
        <div style={{ marginBottom: 32 }}>
          <div style={{
            width: 300,
            height: 32,
            background: 'var(--bg-tertiary)',
            borderRadius: 6,
            marginBottom: 12,
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          }} />
          <div style={{
            width: 400,
            height: 16,
            background: 'var(--bg-tertiary)',
            borderRadius: 4,
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          }} />
        </div>

        {/* Summary cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonSummaryCard key={i} />
          ))}
        </div>

        {/* Main content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div style={{
                width: 200,
                height: 20,
                background: 'var(--bg-tertiary)',
                borderRadius: 4,
                marginBottom: 16,
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }} />
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonClusterCard key={i} />
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar skeleton */}
          <div className="space-y-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-coros-status-error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  // Filter requests by status
  const filterByStatus = (items) => {
    const filter = STATUS_FILTERS[statusFilter];
    if (!filter.statuses) return items;
    return items.filter(c => filter.statuses.includes(c.status));
  };

  const filteredRequests = filterByStatus(data.top_requests);
  const filteredIssues = filterByStatus(data.top_issues || []);
  const visibleRequests = showAllRequests ? filteredRequests : filteredRequests.slice(0, 10);
  const visibleIssues = showAllIssues ? filteredIssues : filteredIssues.slice(0, 5);

  // Counts for filter tabs
  const countByFilter = (items) => ({
    needs_attention: items.filter(c => STATUS_FILTERS.needs_attention.statuses.includes(c.status)).length,
    tracked: items.filter(c => STATUS_FILTERS.tracked.statuses.includes(c.status)).length,
    all: items.length,
  });
  const requestCounts = countByFilter(data.top_requests);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header — branded hero band with COROS angular accent */}
      <div className="accent-cut" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12,
        background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
        borderRadius: 14, padding: '20px 24px', marginBottom: 32,
        boxShadow: 'var(--shadow-xs)',
      }}>
        <div>
          <h1 className="text-2xl font-bold text-coros-text-primary">Product Feedback Dashboard</h1>
          <p className="text-coros-text-muted mt-1">
            {data.period.label} • Last updated {data.last_updated ? new Date(data.last_updated).toLocaleString() : 'N/A'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', zIndex: 1 }}>
          <PeriodSelector value={period} onChange={changePeriod} />
          <ExportMenu period={period} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard
          title="Total Feedback"
          value={data.summary.total_feedback}
          change={data.summary.total_feedback_change}
          color="blue"
        />
        <SummaryCard
          title="Feature Requests"
          value={data.summary.feature_requests}
          change={data.summary.feature_requests_change}
          color="green"
        />
        <SummaryCard
          title="Issues"
          value={data.summary.issues}
          change={data.summary.issues_change}
          color="red"
        />
        <SummaryCard
          title="Competitor Mentions"
          value={data.summary.competitor_mentions}
          change={data.summary.competitor_mentions_change}
          color="orange"
        />
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column: Top Requests */}
        <div className="lg:col-span-2 space-y-6">
          {/* Top Feature Requests */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              <h2 className="text-xl font-semibold text-coros-text-primary" style={{ margin: 0 }}>
                Feature Requests
              </h2>
              <StatusFilterTabs value={statusFilter} onChange={setStatusFilter} counts={requestCounts} />
            </div>
            {filteredRequests.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                  No requests with status "{STATUS_FILTERS[statusFilter].label}"
                </p>
                <button
                  onClick={() => setStatusFilter('all')}
                  style={{
                    marginTop: 8, fontSize: 13, fontWeight: 600,
                    color: 'var(--coros-red)', background: 'none',
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Show all requests →
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {visibleRequests.map((cluster) => (
                    <ClusterCard key={cluster.id} cluster={cluster} />
                  ))}
                </div>
                {filteredRequests.length > 10 && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowAllRequests(!showAllRequests)}
                      className="text-coros-red hover:text-coros-red-hover text-sm font-medium"
                    >
                      {showAllRequests
                        ? 'Show less ↑'
                        : `View all ${filteredRequests.length} requests ↓`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Top Issues */}
          {data.top_issues && data.top_issues.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-coros-text-primary mb-4">
                Top Issues
              </h2>
              <div className="space-y-3">
                {visibleIssues.map((cluster) => (
                  <ClusterCard key={cluster.id} cluster={cluster} />
                ))}
              </div>
              {data.top_issues.length > 5 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowAllIssues(!showAllIssues)}
                    className="text-coros-red hover:text-coros-red-hover text-sm font-medium"
                  >
                    {showAllIssues
                      ? 'Show less ↑'
                      : `View all ${data.top_issues.length} issues ↓`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right column: Sidebar */}
        <div className="space-y-6">
          {/* Emerging Trends */}
          {data.emerging_trends && data.emerging_trends.length > 0 && (
            <EmergingTrendsCard trends={data.emerging_trends} />
          )}

          {/* Regional Summary */}
          {data.regional_summary && (
            <RegionalSummary summary={data.regional_summary} />
          )}

          {/* Recent Activity */}
          <RecentActivityCard />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
